import React, { FunctionComponent } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { IContextInfo } from '../../rs-emd-ui-modules/src/models/master/context-info.model';
import { SSRContext } from '../app/ssr-context';
import BasketWrapper from '../basket-purchase/basket-wrapper-component';
import OrderConfirmationWrapper from '../basket-purchase/order-confirmation-wrapper-component';
import PurchaseWrapper from '../basket-purchase/purchase-wrapper-component';
import QuoteConfirmationPageWrapper from '../basket-purchase/quote-confirmation-wrapper-component';
import CampaignPageWrapper from '../campaigns/campaign-page/campaign-page-wrapper-component';
import CLPWrapper from '../clp/clp-wrapper-component';
import CompareAlternativesWrapper from '../compare-alternatives/compare-alternatives-wrapper-component';
import Error404PageWrapper from '../error-pages/error-404-page-wrapper-component';
import TechnicalErrorWrapper from '../error-pages/technical-error-wrapper-component';
import PDPWrapper from '../pdp/pdp-wrapper-component';
import NoResultsWrapper from '../plp/no-results-wrapper-component';
import PLPWrapper from '../plp/plp-wrapper-component';
import SRPWrapper from '../srp/srp-wrapper-component';
import ForgotPasswordWrapper from '../user/forgot-password-wrapper-component';
import LoginWrapper from '../user/login-wrapper-component';
import MyAccountWrapper from '../user/my-account/my-account-wrapper-component';
import ProfileWrapper from '../user/profile-wrapper-component';
import RegistrationWrapper from '../user/registration-wrapper-component';
import ResetPasswordWrapper from '../user/reset-password-wrapper-component';

type RouterProps = {
  contextInfo: IContextInfo;
};

export const Router: FunctionComponent<RouterProps> = ({ contextInfo }) => {
  const location = useLocation();
  const params = useParams(); // only used for key example in PDPWrapper

  return (
    <Routes>
      <Route path=':langCode?/user/login' element={<LoginWrapper />} />
      <Route path=':langCode?/user/forgotPassword' element={<ForgotPasswordWrapper />} />
      <Route path=':langCode?/user/resetPassword' element={<ResetPasswordWrapper />} />
      <Route path=':langCode?/user/register' element={<RegistrationWrapper contextInfo={contextInfo} />} />
      <Route path=':langCode?/user/onboarding/profile' element={<ProfileWrapper contextInfo={contextInfo} onboarding />} />
      <Route path=':langCode?/user/quoteConfirmation' element={<QuoteConfirmationPageWrapper contextInfo={contextInfo} />} />
      <Route path=':langCode?/user/*' element={<MyAccountWrapper contextInfo={contextInfo} />} />

      {/* BasketWrapper: remount on query change */}
      <Route path=':langCode?/cart' element={<BasketWrapper key={location.search} contextInfo={contextInfo} />} />

      <Route path=':langCode?/purchase' element={<PurchaseWrapper contextInfo={contextInfo} />} />
      <Route path=':langCode?/confirmation' element={<OrderConfirmationWrapper contextInfo={contextInfo} />} />
      <Route path=':langCode?/category/*' element={<CLPWrapper contextInfo={contextInfo} />} />

      <Route path=':langCode?/product/*' element={<PDPWrapper key={params.stockCode} contextInfo={contextInfo} />} />

      <Route path=':langCode?/browse/*' element={<PLPWrapper contextInfo={contextInfo} />} />

      {/* SRPWrapper: remount on query string change */}
      <Route path=':langCode?/productlist/search' element={<SRPWrapper key={location.search} contextInfo={contextInfo} />} />

      <Route path=':langCode?/compare-alternatives/:stockCode' element={<CompareAlternativesWrapper />} />

      {/* Will check if any campaign pages satisfy the URL. */}
      <Route path=':langCode?/content/*' element={<CampaignPageWrapper contextInfo={contextInfo} />} />
      <Route path=':langCode?/ourbrands/*' element={<CampaignPageWrapper contextInfo={contextInfo} />} />

      {/* Error pages */}
      <Route path=':langCode?/productpage/ProductNotFound' element={<NoResultsWrapper />} />

      <Route path=':langCode?/error404/error404' element={<Error404PageWrapper />} />

      <Route path=':langCode?/error/error' element={<TechnicalErrorWrapper />} />

      {/* This route is needed to avoid hitting the 404 page during the healthcheck */}
      <Route path='/' element={<CampaignPageWrapper contextInfo={contextInfo} />} />

      {/* If none of the previous routes render anything,
      this route acts as a fallback - shouldn't get to this point.

      Important: A route with path="/" will *always* match
      the URL because all URLs begin with a /. So that's
      why we put this one last of all */}
      <Route
        path='*'
        element={
          <SSRContext.Consumer>
            {(ctx) => {
              if (ctx) ctx.statusCode = 404;
              return <Navigate to='/error404/error404' replace />;
            }}
          </SSRContext.Consumer>
        }
      />
    </Routes>
  );
};
